import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { useAuth } from '@/context/auth';
import { useToast } from '@/hooks/use-toast';
import { getEscorts } from '@/data/mockData';
import { EscortProfile, UserRole, SubscriptionTier } from '@/types';
import { importEscortsToSupabase } from '@/utils/importMockDataToSupabase';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Info } from 'lucide-react';
import AdminLoadingState from '@/components/admin/AdminLoadingState';
import AdminHeader from '@/components/admin/AdminHeader';
import StatsCards from '@/components/admin/StatsCards';
import AdminTabs from '@/components/admin/AdminTabs';
import { supabase } from '@/integrations/supabase/client';
import { setupSupabaseApp } from '@/utils/setupSupabase';
import UserManagementTab from '@/components/admin/UserManagementTab';
import ReportedContentTab from '@/components/admin/ReportedContentTab';
import VerificationRequestsTab from '@/components/admin/VerificationRequestsTab';
import SupabaseVerification from '@/components/admin/SupabaseVerification';
import { useLanguageUtils } from '@/context/language-context';
import { useTranslation } from 'react-i18next';

const Admin = () => {
  const [escorts, setEscorts] = useState<EscortProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [isSynchronizing, setIsSynchronizing] = useState(false);
  const { user, synchronizeAccounts } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (user.role !== 'admin') {
      navigate('/');
      toast({
        title: 'Access Denied',
        description: 'Only administrators can access this page.',
        variant: 'destructive',
      });
      return;
    }

    setupSupabaseApp()
      .then(result => {
        if (!result.success) {
          console.error("Failed to initialize storage buckets:", result.error);
          toast({
            title: "Storage Error",
            description: "Failed to initialize Supabase storage. Some features may not work.",
            variant: "destructive",
          });
        } else {
          console.log("Storage buckets initialized successfully");
        }
      })
      .catch(err => {
        console.error("Error initializing storage:", err);
        toast({
          title: "Storage Error",
          description: "Error initializing storage: " + (err.message || "Unknown error"),
          variant: "destructive",
        });
      });

    const loadRemainingData = async () => {
      setLoading(true);
      try {
        const escortProfiles = await getEscorts();
        
        setEscorts(escortProfiles);
      } catch (error) {
        console.error('Error loading remaining admin data (escorts):', error);
        toast({
          title: 'Error',
          description: 'Failed to load escort data for stats.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadRemainingData();
  }, [user, navigate, toast]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Implementation of handleImageUpload
  };

  const handleImportData = async () => {
    if (isImporting) return;
    
    setIsImporting(true);
    toast({
      title: "Importation en cours",
      description: "Les données mockées sont en cours d'importation vers Supabase...",
    });
    
    try {
      const result = await importEscortsToSupabase();
      
      toast({
        title: "Importation terminée",
        description: `${result.count} profils ont été importés vers Supabase.`,
        variant: result.success ? "default" : "destructive",
      });
      
      if (result.success && result.count > 0) {
        const updatedEscorts = await getEscorts();
        setEscorts(updatedEscorts);
      }
    } catch (error) {
      console.error("Erreur lors de l'importation:", error);
      toast({
        title: "Erreur d'importation",
        description: error.message || "Une erreur s'est produite lors de l'importation des données.",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };
  
  const handleSyncAccounts = async () => {
    if (isSynchronizing) return;
    
    setIsSynchronizing(true);
    
    try {
      toast({
        title: "Synchronisation en cours",
        description: "Synchronisation des comptes en cours...",
      });
      
      const accounts = await synchronizeAccounts();
      
      toast({
        title: "Synchronisation terminée",
        description: `${accounts?.length || 0} comptes ont été synchronisés avec Supabase.`,
      });
    } catch (error) {
      console.error("Erreur lors de la synchronisation:", error);
      toast({
        title: "Erreur de synchronisation",
        description: "Une erreur s'est produite lors de la synchronisation des comptes.",
        variant: "destructive",
      });
    } finally {
      setIsSynchronizing(false);
    }
  };

  if (loading && !user) {
    return <Layout><AdminLoadingState /></Layout>;
  }

  const stats = {
    totalUsers: 0,
    totalEscorts: escorts.length,
    pendingVerifications: 0,
    pendingReports: 0,
  };

  const tabsConfig = [
    {
      value: 'users',
      label: t('admin.tabs.users') || 'Users',
      content: <UserManagementTab />
    },
    {
      value: 'reports',
      label: t('admin.tabs.reports') || 'Reported Content',
      content: <ReportedContentTab />
    },
    {
      value: 'verifications',
      label: t('admin.tabs.verifications') || 'Verification Requests',
      content: <VerificationRequestsTab />
    },
    {
      value: 'supabase',
      label: t('admin.tabs.supabase') || 'Supabase Status',
      content: <SupabaseVerification />
    },
  ];

  return (
    <Layout>
      <div className="container mx-auto py-8">
        <AdminHeader 
          onImportData={handleImportData} 
          onSyncAccounts={handleSyncAccounts}
          isImporting={isImporting}
          isSynchronizing={isSynchronizing}
        />
        
        <StatsCards stats={stats} />

        <div className="mt-8">
          <AdminTabs tabsConfig={tabsConfig} />
        </div>
      </div>
    </Layout>
  );
};

export default Admin;
